import { IPurchaseState, PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { REWARD_CODE_PREFIX } from "@smartrr/shared/utils/getDiscounts";

export const checkIfVariantIsInUpcomingOrder = (
  upcomingDelivery: ScheduledDeliveryGroupWithPurchSt,
  variant: LoyaltyApi.RewardProduct.Type
): IPurchaseStateLineItem | undefined => {
  const foundItem = upcomingDelivery.purchaseState.stLineItems.find(
    lineItem => lineItem.vnt.id === variant.variantId
  );
  if (foundItem?.isRedeemed) {
    return foundItem;
  }
};

export const checkIfIncentiveIsInUpcomingOrder = (
  upcomingDelivery: ScheduledDeliveryGroupWithPurchSt
): boolean => {
  return upcomingDelivery.purchaseState.discounts.some(discount => discount.code?.includes(REWARD_CODE_PREFIX));
};

export const isRewardItemInUpcomingOrder = (
  upcomingOrder: PurchaseStateAndDelivery,
  item: LoyaltyApi.VariantOrIncentive.Type
) =>
  isRewardIncentive(item)
    ? checkIfIncentiveIsInUpcomingOrder(upcomingOrder)
    : Boolean(checkIfVariantIsInUpcomingOrder(upcomingOrder, item));

export const checkPurchaseStatesInactiveWithPaused = (purchaseStates: IPurchaseState[]) => {
  const allInactive = purchaseStates.every(purchaseState => purchaseState.purchaseStateStatus !== "ACTIVE");
  const checkIfPaused = allInactive
    ? purchaseStates.some(purchaseState => purchaseState.purchaseStateStatus === "PAUSED")
    : false;
  return checkIfPaused;
};

export const getNearestUnpauseDate = (pausedPurchaseStates: IPurchaseState[]): IPurchaseState | null => {
  if (pausedPurchaseStates.length === 0) {
    return null;
  }

  const purchaseStatesWithUnpauseDate = pausedPurchaseStates.filter(purchaseState => purchaseState.unpauseDate);
  if (purchaseStatesWithUnpauseDate.length === 0) {
    return pausedPurchaseStates[0];
  }

  const currentDate = new Date();
  let nearestPurchaseState = purchaseStatesWithUnpauseDate[0];
  let minDiff = Math.abs(new Date(nearestPurchaseState.unpauseDate!).getTime() - currentDate.getTime());

  for (const purchaseState of pausedPurchaseStates) {
    const unpauseDate = new Date(purchaseState.unpauseDate);
    const diff = Math.abs(unpauseDate.getTime() - currentDate.getTime());
    if (diff < minDiff) {
      nearestPurchaseState = purchaseState;
      minDiff = diff;
    }
  }

  return nearestPurchaseState;
};

export const convertNumberToMonth = (month: number) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (month < 1 || month > 12) {
    throw new Error("Invalid month number");
  }
  return monthNames[month];
};

export const getDateSuffix = (date: number) => {
  if (date >= 11 && date <= 13) {
    return "th";
  }
  switch (date % 10) {
    case 1: {
      return "st";
    }
    case 2: {
      return "nd";
    }
    case 3: {
      return "rd";
    }
    default: {
      return "th";
    }
  }
};

export const getMonthAndDayFromDate = (dateString: string) => {
  if (dateString) {
    const convertedDate = new Date(dateString);
    const month = convertedDate.getMonth();
    const date = convertedDate.getDate();
    const convertedMonth = convertNumberToMonth(month);
    const dateSuffix = getDateSuffix(date);
    return `${convertedMonth} ${date}${dateSuffix}`;
  }
};

export const isRewardIncentive = (
  x: LoyaltyApi.Incentive.Type | LoyaltyApi.RewardProduct.Type
): x is LoyaltyApi.Incentive.Type => x.rewardType === "INCENTIVE";
